import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, CalendarPlus, Loader2 } from 'lucide-react';
import DispatchToolPicker from './DispatchToolPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30';
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA'); };

/** Dated add-on tool list for a job — lists under the original on the card, the print sheet and the crib's box. */
export const ToolAmendmentModal = ({ req, amendment, onClose, onSaved }) => {
  const [f, setF] = useState({
    date: amendment?.date || tomorrow(),
    tools: amendment?.tools || [],
    free_text: amendment?.free_text || '',
    note: amendment?.note || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.date) return toast.error('Pick the day this list is for');
    if (!f.tools.length && !f.free_text.trim()) return toast.error('Add at least one tool or type the list');
    setBusy(true);
    try {
      const url = `${API}/dispatch/requests/${req.id}/tool-amendments${amendment ? `/${amendment.id}` : ''}`;
      const r = amendment ? await axios.put(url, f, auth()) : await axios.post(url, f, auth());
      toast.success(amendment ? 'Tool list amendment updated' : `Tool list for ${new Date(`${f.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} added — the crib has it`);
      onSaved(r.data.tool_amendments);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="tool-amendment-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mt-6 mb-10">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-bold text-[#1e3a5f] inline-flex items-center gap-2"><CalendarPlus size={18} /> {amendment ? 'Edit tool list amendment' : 'Tool list amendment'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{req.request_number} · WO {req.work_order_number || '—'} · {req.customer_name}. Lists under the original tool list for that day; the crib gets it right away.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="tool-amendment-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="grid sm:grid-cols-[180px_1fr] gap-3">
            <label className="block">
              <span className="block text-xs font-semibold text-gray-600 mb-1">For which day</span>
              <input type="date" value={f.date} onChange={(e) => set('date', e.target.value)} className={inputCls} data-testid="tool-amendment-date" />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-gray-600 mb-1">Note for the crib (optional)</span>
              <input value={f.note} onChange={(e) => set('note', e.target.value)} placeholder="e.g. second pump going in Tuesday" className={inputCls} data-testid="tool-amendment-note" />
            </label>
          </div>
          <div>
            <span className="block text-xs font-semibold text-gray-600 mb-1">Pick from the tool list</span>
            <DispatchToolPicker value={f.tools} onChange={(v) => set('tools', v)} testid="tool-amendment-picker" />
          </div>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-600 mb-1">Or just type the list (one per line, e.g. "2x 10' ladder")</span>
            <textarea value={f.free_text} onChange={(e) => set('free_text', e.target.value)} className={`${inputCls} h-28 resize-none font-mono`} placeholder={'4 ratchet straps\n1 come-along\n2 - 20\' chains'} data-testid="tool-amendment-text" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100" data-testid="tool-amendment-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-[#1e3a5f] hover:bg-[#162c48] disabled:opacity-60 inline-flex items-center gap-2" data-testid="tool-amendment-save">
            {busy && <Loader2 size={14} className="animate-spin" />}{amendment ? 'Save changes' : 'Add list'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** The dated lists under the original tools on a request card. */
export const ToolAmendments = ({ req, canEdit, userId, isDispatch, onEdit, onChanged, onPrint }) => {
  const list = [...(req.tool_amendments || [])].sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.created_at || '').localeCompare(b.created_at || ''));
  if (!list.length) return null;
  const remove = async (a) => {
    if (!window.confirm(`Remove the ${fmt(a.date)} tool list?`)) return;
    try { await axios.delete(`${API}/dispatch/requests/${req.id}/tool-amendments/${a.id}`, auth()); toast.success('Removed'); onChanged((req.tool_amendments || []).filter((x) => x.id !== a.id)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
  };
  return (
    <div className="mt-2 space-y-1.5" data-testid={`dr-amendments-${req.id}`}>
      {list.map((a) => {
        const lines = (a.free_text || '').split(/[\n;]+/).map((s) => s.trim()).filter(Boolean);
        return (
          <div key={a.id} className="rounded-lg border border-dashed border-amber-300 bg-amber-50/40 px-3 py-2" data-testid={`dr-amendment-${a.id}`}>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-bold text-amber-900">+ Amendment · {fmt(a.date)}</span>
              <span className="text-gray-500">by {a.created_by_name}{a.note ? ` — ${a.note}` : ''}</span>
              <span className="flex-1" />
              <button onClick={() => onPrint(a)} className="text-[#1e3a5f] font-semibold hover:underline" data-testid={`dr-amendment-print-${a.id}`}>Print</button>
              {canEdit && <button onClick={() => onEdit(a)} className="text-[#1e3a5f] font-semibold hover:underline" data-testid={`dr-amendment-edit-${a.id}`}>Edit</button>}
              {(isDispatch || a.created_by === userId) && <button onClick={() => remove(a)} className="text-red-600 font-semibold hover:underline" data-testid={`dr-amendment-delete-${a.id}`}>Remove</button>}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(a.tools || []).map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 bg-white text-amber-800 border border-amber-200 text-xs font-medium px-2 py-0.5 rounded">
                  {t.qty > 1 && <span className="opacity-70">{t.qty}×</span>}{t.label}
                </span>
              ))}
              {lines.map((l, i) => (
                <span key={`t${i}`} className="inline-flex items-center gap-1 bg-white text-slate-700 border border-slate-200 text-xs font-medium px-2 py-0.5 rounded italic">{l}</span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '');
